import { BaseInvitePeopleAnalytics } from '../../analytics';
import { FetchError } from '../../clients/helpers';
import type { CollectionGroupType } from '../../utils/twcForCoreInvites/getGroupedProductOptions';
import { AnalyticsAction, AnalyticsActionSubject, type ProductSelectOption } from '../../types';

export class ProductSelectAnalytics extends BaseInvitePeopleAnalytics {
	productSelectRendered(productOptions: ProductSelectOption[]): void {
		const { userRole, source } = this.commonAttributes;

		this.fireUI({
			actionSubject: AnalyticsActionSubject.DROPDOWN,
			actionSubjectId: 'customizedProductSelect',
			action: AnalyticsAction.RENDERED,
			attributes: {
				integration: source,
				source,
				userRole,
				productOptions,
			},
		});
	}

	selectAllClicked(productsSelected: string[]): void {
		const { productTitle, source, userRole } = this.commonAttributes;

		this.fireUI({
			actionSubject: AnalyticsActionSubject.DROPDOWN_ITEM,
			action: AnalyticsAction.CLICKED,
			actionSubjectId: 'selectAll',
			attributes: {
				integration: source,
				source,
				product: productTitle.toLowerCase(),
				productsSelected,
				permissionLevel: userRole,
			},
		});
	}

	getAvailableProductsSucceeded(resourceAri: string, availableResourcesAri: string[]): void {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.INVITE_CAPABILITIES,
			actionSubjectId: resourceAri,
			action: AnalyticsAction.SUCCEEDED,
			attributes: {
				availableResourcesAri,
			},
		});
	}

	// Replaces getAvailableProductsFailed when PUMA-540 is cleaned up
	_getAvailableProductsFailed(resourceAri: string, errorMsg?: string, statusCode?: number): void {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.INVITE_CAPABILITIES,
			actionSubjectId: resourceAri,
			action: AnalyticsAction.FAILED,
			attributes: {
				message: errorMsg,
				statusCode,
			},
		});
	}

	/**
	 * @deprecated
	 * will be deleted when puma-540-expose-invite-capabilities is cleaned up
	 * replaced by the newer _getAvailableProductsFailed
	 **/
	getAvailableProductsFailed(resourceAri: string, error: unknown): void {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.INVITE_CAPABILITIES,
			actionSubjectId: resourceAri,
			action: AnalyticsAction.FAILED,
			attributes: {
				message: error instanceof Error ? error.message : undefined,
				statusCode: error instanceof FetchError ? error.responseCode : undefined,
			},
		});
	}

	getCollectionsSucceeded(resourceAri: string, hasTeamworkCollection: boolean): void {
		const { productTitle, source, subProduct, jiraSubProduct, userRole } = this.commonAttributes;
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.GET_COLLECTIONS,
			actionSubjectId: resourceAri,
			action: AnalyticsAction.SUCCEEDED,
			attributes: {
				hasTeamworkCollection,
				productTitle,
				source,
				subProduct,
				jiraSubProduct,
				userRole,
			},
		});
	}

	getCollectionsFailed(resourceAri: string): void {
		const { productTitle, source, subProduct, jiraSubProduct, userRole } = this.commonAttributes;
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.GET_COLLECTIONS,
			actionSubjectId: resourceAri,
			action: AnalyticsAction.FAILED,
			attributes: { productTitle, source, subProduct, jiraSubProduct, userRole },
		});
	}

	groupsRendered(
		productsSelected: string[],
		options: (ProductSelectOption | CollectionGroupType<ProductSelectOption>)[],
	): void {
		const { productTitle, source, subProduct, jiraSubProduct, userRole } = this.commonAttributes;
		this.fireUI({
			actionSubject: AnalyticsActionSubject.DROPDOWN,
			actionSubjectId: 'twcForCoreInvites',
			action: AnalyticsAction.RENDERED,
			attributes: {
				productsSelected,
				productTitle,
				source,
				subProduct,
				jiraSubProduct,
				userRole,
				options,
			},
		});
	}
}
